import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'

interface CookiePreferences {
  necessary: boolean
  analytics: boolean
  marketing: boolean
}

interface CookieConsentContextValue {
  showBanner: boolean
  preferences: CookiePreferences
  acceptAll: () => void
  rejectAll: () => void
  savePreferences: (prefs: CookiePreferences) => void
}

const STORAGE_KEY = 'labinstruments-cookies-consent-v1'

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null)

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [showBanner, setShowBanner] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences)

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      setPreferences(JSON.parse(stored))
      setShowBanner(false)
    } else {
      setShowBanner(true)
    }
  }, [])

  const save = (prefs: CookiePreferences) => {
    setPreferences(prefs)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
    setShowBanner(false)
  }

  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true })
  const rejectAll = () => save({ necessary: true, analytics: false, marketing: false })
  const savePreferences = (prefs: CookiePreferences) => save({ ...prefs, necessary: true })

  return (
    <CookieConsentContext.Provider value={{ showBanner, preferences, acceptAll, rejectAll, savePreferences }}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext)
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider')
  }
  return context
}
